import {parse} from 'acorn';
import {formulaPath, formulaSource, visitFormula} from '../components/formula-editor/formula-language';

/**
 * Formulas in formulas.
 *
 * Authors write formulas that use other formulas (`formula.bonus`), also in statement form (`if (…) { … } else { … }`).
 * The filling app only recalculates a formula when one of its direct inputs changes and has no order between formulas,
 * so a formula that uses another formula would keep a stale value. Instead of changing the app, the editor compiles:
 * every `formula.X` reference is replaced by the expression of X, recursively. The app gets an expression that only
 * uses real inputs (answers, scoring, extern, …) and works unchanged with every app version.
 *
 * Statements cannot be part of an expression, so a used formula in statement form is converted to the equivalent
 * conditional expression first (`if (c) { a } else { b }` becomes `((c) ? (a) : (b))`). The conversion keeps the
 * completion value semantics of the app's script execution: a missing branch or an empty block yields `undefined`,
 * like the script would.
 *
 * The editor keeps working with the authored form. Only the payload that leaves the editor carries the compiled
 * expression, the authored form travels along in `source` and is restored when the questionnaire is loaded again.
 *
 * Nothing in here executes a formula. Everything works on the source text and its syntax tree.
 */

/** Why a formula could not be compiled */
export type FormulaCompileErrorType = 'syntax' | 'circular' | 'unknownFormula' | 'notInlinable' | 'brokenDependency';

/** Describes why a formula could not be compiled */
export interface FormulaCompileError {
  type: FormulaCompileErrorType;
  /** the formula that causes the problem (unknown, not inlinable or broken dependency) */
  detail?: string;
  /** the formulas that use each other, in order, for circular errors */
  cycle?: string[];
}

/** Compile result of one formula */
export interface FormulaCompileResult {
  /** expression for the app: the authored source with all formula references resolved. Missing on error */
  expression?: string;
  error?: FormulaCompileError;
  /** formulas this formula uses directly */
  dependencies: string[];
  /** formulas that use this formula directly */
  usedBy: string[];
}

/** A `formula.X` reference in the source, with its range in the parsed code */
interface FormulaReference {
  name: string;
  start: number;
  end: number;
}

/** Parsed formula: unwrapped code, its syntax tree, comments and formula references */
interface ParsedFormula {
  code: string;
  tree: any;
  comments: {start: number; end: number}[];
  references: FormulaReference[];
}

/** Marks "no completion value", e.g. a body that only declares helper variables */
const EMPTY = Symbol('empty');

/**
 * Parses a formula source. Returns null on syntax errors.
 *
 * @param {string} source
 * @returns {ParsedFormula}
 */
function parseSource(source: string): ParsedFormula {
  const code = formulaSource((source || '') + '').code;
  const comments: {start: number; end: number}[] = [];

  try {
    const tree = parse(code, {ecmaVersion: 'latest', preserveParens: true, onComment: comments as any});
    const references: FormulaReference[] = [];

    visitFormula(tree, node => {
      if (node.type !== 'MemberExpression') { return; }
      const path = formulaPath(node).split('.');
      if (path.length === 2 && path[0] === 'formula' && path[1]) {
        references.push({name: path[1], start: node.start, end: node.end});
      }
    });

    return {code, tree, comments, references};
  } catch (error) {
    return null;
  }
}

/**
 * Replaces ranges of the given text. Ranges are relative to `offset` and must not overlap
 *
 * @param {string} text
 * @param {number} offset
 * @param {{start: number, end: number, text: string}[]} replacements
 * @returns {string}
 */
function splice(text: string, offset: number, replacements: {start: number; end: number; text: string}[]): string {
  return replacements
    .slice()
    .sort((a, b) => b.start - a.start)
    .reduce((result, item) => result.slice(0, item.start - offset) + item.text + result.slice(item.end - offset), text);
}

/**
 * Names of the formulas that the given source uses (`formula.X`), unique and in order of appearance
 *
 * @param {string} source
 * @returns {string[]}
 */
export function formulaDependencies(source: string): string[] {
  const parsed = parseSource(source);
  return parsed ? Array.from(new Set(parsed.references.map(reference => reference.name))) : [];
}

/**
 * Range of the first reference to the given formula in the raw source, null when there is none
 *
 * @param {string} source
 * @param {string} name
 * @returns {{start: number, end: number}}
 */
export function formulaReferenceRange(source: string, name: string): {start: number; end: number} {
  const parsed = parseSource(source);
  const reference = parsed && parsed.references.find(item => item.name === name);
  const offset = formulaSource((source || '') + '').offset;
  return reference ? {start: reference.start + offset, end: reference.end + offset} : null;
}

/**
 * Renames references to a formula (`formula.from` becomes `formula.to`). Keeps the source untouched otherwise,
 * also when it can not be parsed
 *
 * @param {string} source
 * @param {string} from
 * @param {string} to
 * @returns {string}
 */
export function renameFormulaReferences(source: string, from: string, to: string): string {
  const parsed = parseSource(source);

  if (!parsed || !parsed.references.some(reference => reference.name === from)) {
    return source;
  }

  const offset = formulaSource(source).offset;
  const replacements = parsed.references
    .filter(reference => reference.name === from)
    .map(reference => ({start: reference.start + offset, end: reference.end + offset, text: `formula.${to}`}));

  return splice(source, 0, replacements);
}

/**
 * Converts a formula in statement form into an equivalent expression. Expressions stay expressions.
 * Returns null when there is no safe conversion (loops, reassigned helper variables, declarations in branches, …).
 *
 * Supported: expression statements, if / else if / else, blocks, empty statements and helper variables declared
 * once at the top (`var x = …;`), which are inlined where they are used.
 *
 * @param {string} source formula without `formula.X` references
 * @returns {string}
 */
export function formulaToExpression(source: string): string {
  const parsed = parseSource(source);

  if (!parsed) {
    return null;
  }

  const {code, tree, comments} = parsed;
  const locals = new Map<string, string>();
  let reassigned = false;

  // helper variables must keep their initial value, otherwise inlining would change the result
  visitFormula(tree, node => {
    const target = node.type === 'AssignmentExpression' ? node.left : node.type === 'UpdateExpression' ? node.argument : null;
    target && target.type === 'Identifier' && (reassigned = true);
  });

  if (reassigned) {
    return null;
  }

  /** Source text of a node without comments, with helper variables replaced by their value */
  const text = (node: any): string => {
    const replacements: {start: number; end: number; text: string}[] = comments
      .filter(comment => comment.start >= node.start && comment.end <= node.end)
      .map(comment => ({start: comment.start, end: comment.end, text: ' '}));

    visitFormula(node, (child, parent) => {
      if (child.type !== 'Identifier' || !locals.has(child.name)) { return; }
      const isProperty = parent && parent.type === 'MemberExpression' && parent.property === child && !parent.computed;
      const isKey = parent && parent.type === 'Property' && parent.key === child && !parent.computed && !parent.shorthand;
      !isProperty && !isKey && replacements.push({start: child.start, end: child.end, text: locals.get(child.name)});
    });

    return splice(code.slice(node.start, node.end), node.start, replacements).trim();
  };

  /** Converts a branch of an if statement. Declarations are not allowed in branches */
  const branch = (node: any): string | typeof EMPTY => {
    if (!node || node.type === 'EmptyStatement') { return EMPTY; }
    if (node.type === 'BlockStatement') { return body(node.body, false); }
    return body([node], false);
  };

  /** Converts a list of statements to the expression of their completion value */
  const body = (statements: any[], allowDeclarations: boolean): string | typeof EMPTY => {
    let result: string | typeof EMPTY = EMPTY;

    for (const statement of statements) {
      if (statement.type === 'EmptyStatement') {
        continue;
      }

      if (statement.type === 'VariableDeclaration' && allowDeclarations) {
        for (const declaration of statement.declarations) {
          if (declaration.id.type !== 'Identifier' || !declaration.init || locals.has(declaration.id.name)) {
            throw new Error('unsupported declaration');
          }
          locals.set(declaration.id.name, `(${text(declaration.init)})`);
        }
        continue;
      }

      if (statement.type === 'ExpressionStatement') {
        result = `(${text(statement.expression)})`;
        continue;
      }

      if (statement.type === 'IfStatement') {
        const consequent = branch(statement.consequent);
        const alternate = branch(statement.alternate);
        // an if statement always completes: without a matching branch with undefined
        // branches are parenthesized already (expressions) or nested conditionals
        result = `((${text(statement.test)}) ? ${consequent === EMPTY ? 'undefined' : consequent}` +
          ` : ${alternate === EMPTY ? 'undefined' : alternate})`;
        continue;
      }

      if (statement.type === 'BlockStatement') {
        const block = body(statement.body, false);
        block !== EMPTY && (result = block);
        continue;
      }

      throw new Error(`unsupported statement: ${statement.type}`);
    }

    return result;
  };

  try {
    const result = body(tree.body, true);
    return result === EMPTY ? 'undefined' : result;
  } catch (error) {
    return null;
  }
}

/**
 * Compiles all formulas of a questionnaire: resolves `formula.X` references recursively, detects unknown formulas
 * and formulas that use each other. Formulas without references keep their source unchanged.
 *
 * @param {{[name: string]: string}} sources authored source per formula name
 * @returns {{[name: string]: FormulaCompileResult}}
 */
export function compileFormulas(sources: {[name: string]: string}): {[name: string]: FormulaCompileResult} {
  const names = Object.keys(sources);
  const parsed: {[name: string]: ParsedFormula} = {};
  const results: {[name: string]: FormulaCompileResult} = {};

  for (const name of names) {
    parsed[name] = parseSource(sources[name]);
    const dependencies = parsed[name] ? Array.from(new Set(parsed[name].references.map(reference => reference.name))) : [];
    results[name] = {dependencies, usedBy: []};
  }

  for (const name of names) {
    results[name].dependencies.forEach(dependency => results[dependency] && results[dependency].usedBy.push(name));
  }

  // syntax errors and unknown formulas
  for (const name of names) {
    if (!parsed[name]) {
      results[name].error = {type: 'syntax'};
      continue;
    }
    const unknown = results[name].dependencies.find(dependency => !Object.prototype.hasOwnProperty.call(sources, dependency));
    unknown && (results[name].error = {type: 'unknownFormula', detail: unknown});
  }

  // formulas that use each other: depth first search, every formula on a cycle gets the cycle
  const state: {[name: string]: 'visiting' | 'done'} = {};
  const stack: string[] = [];
  const visit = (name: string) => {
    state[name] = 'visiting';
    stack.push(name);

    for (const dependency of results[name].dependencies) {
      if (!results[dependency]) { continue; }

      if (state[dependency] === 'visiting') {
        const cycle = [...stack.slice(stack.indexOf(dependency)), dependency];
        cycle.slice(0, -1).forEach(member => !results[member].error && (results[member].error = {type: 'circular', cycle}));
        continue;
      }

      !state[dependency] && visit(dependency);
    }

    stack.pop();
    state[name] = 'done';
  };
  names.forEach(name => !state[name] && visit(name));

  // expression form of a formula with all references resolved, used where other formulas reference it
  const expressions: {[name: string]: string} = {};
  const resolved = (name: string): string => {
    if (results[name].error) { return null; }

    if (!(name in expressions)) {
      // guard against endless recursion, cycles were marked above
      expressions[name] = null;

      for (const dependency of results[name].dependencies) {
        if (resolved(dependency) === null) {
          const broken = results[dependency].error && results[dependency].error.type !== 'notInlinable' ?
            {type: 'brokenDependency' as FormulaCompileErrorType, detail: dependency} :
            {type: 'notInlinable' as FormulaCompileErrorType, detail: dependency};
          results[name].error = broken;
          return null;
        }
      }

      expressions[name] = inline(name);
    }

    return expressions[name];
  };

  /** Source of a formula with every `formula.X` replaced by the resolved expression of X */
  const substituted = (name: string): string => {
    const {code, references} = parsed[name];
    const replacements = references.map(reference => ({start: reference.start, end: reference.end, text: `(${expressions[reference.name]})`}));
    return splice(code, 0, replacements);
  };

  /** Expression form of a formula, after its references were resolved */
  const inline = (name: string): string => {
    const expression = formulaToExpression(substituted(name));
    return expression === null ? null : expression;
  };

  for (const name of names) {
    if (results[name].error) { continue; }

    // resolve all dependencies first, then build the expression for the app
    const ready = results[name].dependencies.every(dependency => resolved(dependency) !== null);

    if (!ready) {
      const dependency = results[name].dependencies.find(item => resolved(item) === null);
      const reason = results[dependency].error;
      results[name].error = reason && reason.type !== 'notInlinable' ?
        {type: 'brokenDependency', detail: dependency} : {type: 'notInlinable', detail: dependency};
      continue;
    }

    // a formula without references stays exactly as authored
    results[name].expression = results[name].dependencies.length ? substituted(name) : sources[name];
  }

  return results;
}

/**
 * Compiles the formula variables of a payload in place: `expression` gets the version for the app, the authored
 * form is kept in `source`. Only formulas that use other formulas get a `source`; formulas with errors stay as
 * authored (the app yields 0 for them, like for every broken formula).
 *
 * @param {{[name: string]: any}} formulaVariables `meta.options.variables.formula` of a payload copy
 */
export function compileFormulaVariables(formulaVariables: {[name: string]: any}): void {
  if (!formulaVariables || typeof formulaVariables !== 'object') {
    return;
  }

  const sources: {[name: string]: string} = {};
  Object.keys(formulaVariables).forEach(name => sources[name] = authoredFormula(formulaVariables[name]));

  const results = compileFormulas(sources);

  Object.keys(formulaVariables).forEach(name => {
    const variable = formulaVariables[name] || (formulaVariables[name] = {});
    const result = results[name];

    if (result.dependencies.length && result.expression) {
      variable.source = sources[name];
      variable.expression = result.expression;
      return;
    }

    variable.expression = sources[name];
    delete variable.source;
  });
}

/**
 * Restores the authored form of formula variables loaded into the editor (`source` back to `expression`)
 *
 * @param {{[name: string]: any}} formulaVariables `meta.options.variables.formula` of a loaded questionnaire
 */
export function restoreFormulaSources(formulaVariables: {[name: string]: any}): void {
  if (!formulaVariables || typeof formulaVariables !== 'object') {
    return;
  }

  Object.keys(formulaVariables).forEach(name => {
    const variable = formulaVariables[name];

    if (variable && typeof variable.source === 'string') {
      variable.expression = variable.source;
      delete variable.source;
    }
  });
}

/**
 * Authored form of a formula variable: its source when compiled before, otherwise its expression
 *
 * @param variable
 * @returns {string}
 */
function authoredFormula(variable: any): string {
  if (!variable) { return ''; }
  const authored = typeof variable.source === 'string' ? variable.source : variable.expression;
  return authored === undefined || authored === null ? '' : authored + '';
}
